import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import WhiteLabelSettings from "../components/WhiteLabelSettings";
import { 
  Building2, MapPin, Mail, Phone, User, Package,
  CheckCircle, XCircle, AlertCircle, Save, Edit, Lock, Image, Upload
} from "lucide-react";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyProfile = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [formData, setFormData] = useState({
    address: "", city: "", province: "",
    contact_email: "", contact_phone: "", contact_person: ""
  });
  const [logoInvoice, setLogoInvoice] = useState(null);
  const [logoPortal, setLogoPortal] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/v2/tenant/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const data = response.data.data;
        setTenant(data.tenant);
        if (data.tenant?.branding?.logo_invoice_url) setLogoInvoice(data.tenant.branding.logo_invoice_url);
        if (data.tenant?.branding?.logo_portal_url) setLogoPortal(data.tenant.branding.logo_portal_url);
        

        setFormData({
          address: data.tenant.address || "",
          city: data.tenant.city || "",
          province: data.tenant.province || "",
          contact_email: data.tenant.contact_email || "",
          contact_phone: data.tenant.contact_phone || "",
          contact_person: data.tenant.contact_person || ""
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Gagal memuat data perusahaan");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/api/v2/tenant/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTenant(response.data.data);
        setEditing(false);
        toast.success("Data perusahaan berhasil diperbarui");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Gagal memperbarui data perusahaan");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file, type) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      if (type === "invoice") setLogoInvoice(base64);
      else setLogoPortal(base64);
      try {
        setUploadingLogo(true);
        const token = localStorage.getItem("token");
        const payload = type === "invoice" ? { logo_invoice_url: base64 } : { logo_portal_url: base64 };
        await axios.patch(`${API_URL}/api/v2/company-settings/logo`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Logo ${type} berhasil diupload`);
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(`Gagal upload logo ${type}`);
      } finally {
        setUploadingLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { icon: CheckCircle, text: "Aktif", className: "bg-green-100 text-green-800" },
      trial: { icon: AlertCircle, text: "Trial", className: "bg-blue-100 text-blue-800" },
      expired: { icon: XCircle, text: "Expired", className: "bg-red-100 text-red-800" },
      suspended: { icon: XCircle, text: "Suspended", className: "bg-gray-100 text-gray-800" }
    };
    const config = statusConfig[status] || statusConfig["trial"];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        <Icon className="w-4 h-4" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-slate-400">Data perusahaan tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100">Pengaturan Perusahaan</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 dark:text-slate-300">Kelola informasi perusahaan dan pengaturan sistem</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowChangePassword(true)} variant="outline" size="sm" className="border-blue-600 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm dark:text-blue-400 dark:hover:bg-blue-900/30">
            <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Ubah Password
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm">
            {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Menyimpan...</>) : (<><Save className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />Simpan Perubahan</>)}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                Informasi Perusahaan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Nama Perusahaan</label>
                <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-slate-900 dark:border-slate-700">
                  <p className="font-semibold text-gray-900 dark:text-slate-100">{tenant.tenant_name}</p>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Hanya dapat diubah oleh Superadmin</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 dark:text-slate-200">
                  <MapPin className="w-4 h-4" />Alamat Lengkap
                </label>
                <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600" rows="3" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Jl. Contoh No. 123" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Kota</label>
                  <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Jakarta" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Provinsi</label>
                  <Input value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} placeholder="DKI Jakarta" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 dark:text-slate-200"><User className="w-4 h-4" />Contact Person</label>
                <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder="Nama PIC" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 dark:text-slate-200"><Mail className="w-4 h-4" />Email</label>
                <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="admin@company.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 dark:text-slate-200"><Phone className="w-4 h-4" />No. WhatsApp</label>
                <Input value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} placeholder="081234567890" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button onClick={() => { setFormData({ address: tenant.address || "", city: tenant.city || "", province: tenant.province || "", contact_email: tenant.contact_email || "", contact_phone: tenant.contact_phone || "", contact_person: tenant.contact_person || "" }); toast.info("Perubahan dibatalkan"); }} variant="outline" className="flex-1">Reset</Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Menyimpan...</>) : (<><Save className="w-4 h-4 mr-2" />Simpan Perubahan</>)}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                Logo Perusahaan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Logo Invoice</label>
                <p className="text-xs text-gray-500 mb-3 dark:text-slate-400">Logo yang akan muncul di invoice/tagihan pelanggan</p>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden dark:bg-slate-800 dark:border-slate-600">
                    {logoInvoice ? <img src={logoInvoice} alt="Logo Invoice" className="w-full h-full object-contain" /> : <Image className="w-8 h-8 text-gray-400" />}
                  </div>
                  <div>
                    <input type="file" id="logo-invoice" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files[0], "invoice")} />
                    <Button variant="outline" size="sm" disabled={uploadingLogo} onClick={() => document.getElementById("logo-invoice").click()}>
                      <Upload className="w-4 h-4 mr-2" />{uploadingLogo ? "Uploading..." : "Upload Logo"}
                    </Button>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG max 2MB</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Logo Portal Pelanggan</label>
                <p className="text-xs text-gray-500 mb-3 dark:text-slate-400">Logo yang akan muncul di halaman login portal pelanggan</p>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden dark:bg-slate-800 dark:border-slate-600">
                    {logoPortal ? <img src={logoPortal} alt="Logo Portal" className="w-full h-full object-contain" /> : <Image className="w-8 h-8 text-gray-400" />}
                  </div>
                  <div>
                    <input type="file" id="logo-portal" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files[0], "portal")} />
                    <Button variant="outline" size="sm" disabled={uploadingLogo} onClick={() => document.getElementById("logo-portal").click()}>
                      <Upload className="w-4 h-4 mr-2" />{uploadingLogo ? "Uploading..." : "Upload Logo"}
                    </Button>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG max 2MB</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Floating Save Button - Always visible when editing */}
          {editing && (
            <Card className="bg-indigo-50 border-indigo-200 dark:bg-indigo-900/30">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <p className="text-sm text-indigo-700 dark:text-indigo-300">Anda sedang dalam mode edit. Jangan lupa simpan perubahan!</p>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                    <Button onClick={() => { setEditing(false); setFormData({ address: tenant.address || "", city: tenant.city || "", province: tenant.province || "", contact_email: tenant.contact_email || "", contact_phone: tenant.contact_phone || "", contact_person: tenant.contact_person || "" }); }} variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-100 w-full sm:w-auto dark:text-indigo-400 dark:hover:bg-indigo-900/40">Batal</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto">
                      {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Menyimpan...</>) : (<><Save className="w-4 h-4 mr-2" />Simpan Perubahan</>)}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(tenant?.subscription_plan?.features?.includes("white_label") || tenant?.white_label_enabled) && <WhiteLabelSettings />}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                Informasi Subscription
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div><p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Status</p>{getStatusBadge(tenant.subscription_status)}</div>
              <div><p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Paket</p><p className="font-semibold text-gray-900 dark:text-slate-100">{tenant.subscription_plan?.plan_name || "N/A"}</p></div>
              <div><p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Maksimal Pelanggan</p><p className="font-semibold text-gray-900 dark:text-slate-100">{tenant.subscription_plan?.max_customers || 0} customers</p></div>
              {tenant.trial_ends_at && (<div><p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Trial Berakhir</p><p className="text-sm text-gray-900 dark:text-slate-100">{new Date(tenant.trial_ends_at).toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" })}</p></div>)}
            </CardContent>
          </Card>
        </div>
      </div>

      <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
};

export default CompanyProfile;
